<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from '~/store'
import { useModal, notUndefined } from '~/shared/useModal'

import Modal from '~/ui/Modal.vue'
import Button from '~/ui/Button.vue'
import Layout from '~/ui/Layout.vue'
import Spacer from '~/ui/Spacer.vue'
import Card from '~/ui/Card.vue'

import type { Channel, PrivacyLevelEnum } from '~/types'
import ChannelUpload from '~/components/channels/UploadForm.vue'
import LibraryUpload from '~/components/library/FileUpload.vue'
import RemoteSearchForm from '~/components/RemoteSearchForm.vue'

const { t } = useI18n()
const store = useStore()

// `?upload` opens the destination picker; `?upload=library|music|podcast`
// deep-links straight to that destination's upload page; `?upload=channel`
// deep-links to the channel upload page, pre-selecting whatever channel the
// linking page put in `store.state.channels.uploadModalConfig.channel`
const { isOpen, value } = useModal('upload', notUndefined)

type UploadDestination
  = | { type: 'channel', channel?: Channel | null, filter?: 'podcast' | 'music' }
    | { type: 'library' }
    | { type: 'remoteChannel' }

type State = {
  uploadDestination?: UploadDestination
  page: typeof pages[number]
  files?: string[]
}

const destinationFromUrl = (): UploadDestination | undefined => {
  const section = value.value
  return section === 'library'
    ? { type: 'library' }
    : section === 'channel'
      ? { type: 'channel', channel: store.state.channels.uploadModalConfig.channel }
      : section === 'music' || section === 'podcast'
        ? { type: 'channel', filter: section }
        : undefined
}

const init = () => {
  const uploadDestination = store.state.channels.uploadModalConfig.channel
    ? { type: 'channel', channel: store.state.channels.uploadModalConfig.channel } as const
    : destinationFromUrl()
  return uploadDestination
    ? { page: 'uploadFiles', uploadDestination } as const
    : { page: 'selectDestination' } as const
}
const state = ref<State>(init())

const pages = ['selectDestination', 'uploadFiles', 'uploadsInProgress'] as const

const goBack = computed(() =>
  state.value.page === 'selectDestination'
    ? undefined
    : () => { state.value.page = pages[pages.indexOf(state.value.page) - 1]! }
)

// Step 1
const destinationSelected = (destination: UploadDestination) =>
  state.value = { ...state.value, uploadDestination: destination, page: 'uploadFiles' }

// Jump to the linked destination whenever the modal is (re)opened while already
// mounted. Watches `isOpen`, not just `value`: a bare `?upload` link and "no
// upload param" both normalize to the same empty `value`, so `isOpen` is what
// reliably signals a fresh open.
watch([value, isOpen], () => {
  if (!isOpen.value) return

  const uploadDestination = destinationFromUrl()
  if (uploadDestination) {
    state.value = { ...state.value, uploadDestination, page: 'uploadFiles' }
  } else {
    // Bare `?upload`: reset to the picker and drop any channel a previous
    // deep link left preset, so it doesn't leak into this new session.
    store.state.channels.uploadModalConfig.channel = null
    state.value = { page: 'selectDestination' }
  }
})

const direction = ref<'forward' | 'backward'>('forward')

watch(state, ({ page }, oldValue) => {
  direction.value
    = pages.indexOf(page) <= pages.indexOf(oldValue.page)
      ? 'backward'
      : 'forward'

  // TODO: Tidy up this dirty dirty hack
  // Also, it only works for the first page
  document.querySelector('#app')?.setAttribute('inert', 'true')
}, { deep: true })

// Step 1.1

// Load the library for the chosen privacy level
const privacyLevel = ref<PrivacyLevelEnum>('me')

const channelUpload = ref()
</script>

<template>
  <Modal
    v-model="isOpen"
    :title="state.page === 'uploadFiles' && state.uploadDestination?.type === 'remoteChannel'
      ? t('components.channels.UploadModal.header.subscribe')
      : ({
        selectDestination: t('components.channels.UploadModal.header.selectDestination'),
        uploadFiles: t('components.channels.UploadModal.header.uploadFiles'),
        uploadsInProgress: t('components.channels.UploadModal.header.processing')
      } as const)[state.page]"
  >
    <template
      v-if="goBack"
      #topleft
    >
      <Button
        class="color-default-ghost shape-square"
        ui-block-size="40"
        :title="t('components.channels.UploadModal.button.previous')"
        @click="goBack"
      >
        <Icon icon="bi:chevron-compact-left" />
      </Button>
      <Spacer grow />
    </template>

    <Transition
      mode="out-in"
      :class="direction"
    >
      <!-- Page content -->
      <!-- Page 1 -->

      <Layout
        v-if="state.page === 'selectDestination'"
        grid
        style="place-content: center"
      >
        <Card
          ui-raised
          :title="t('modals.upload.title.library')"
          icon="bi:upload"
          @click="destinationSelected({ type: 'library' })"
        >
          <template #image>
            <div class="color-primary-solid" :class="$style.imageBackground">
              <Icon icon="bi:headphones" :class="$style.icon" />
            </div>
          </template>
          {{ t('modals.upload.library') }}
        </Card>
        <Card
          ui-raised
          :title="t('modals.upload.title.musicChannel')"
          icon="bi:upload"
          @click="destinationSelected({ type: 'channel', filter: 'music' })"
        >
          <template #image>
            <div class="color-yellow-solid" :class="$style.imageBackground">
              <Icon icon="bi:music-note-beamed" :class="$style.icon" />
            </div>
          </template>
          {{ t('modals.upload.musicChannel') }}
        </Card>
        <Card
          ui-raised
          :title="t('modals.upload.title.podcastChannel')"
          icon="bi:upload"
          @click="destinationSelected({ type: 'channel', filter: 'podcast' })"
        >
          <template #image>
            <div class="color-yellow-solid" :class="$style.imageBackground">
              <Icon icon="bi:mic-fill" :class="$style.icon" />
            </div>
          </template>
          {{ t('modals.upload.podcastChannel') }}
        </Card>
        <Card
          ui-raised
          :title="t('modals.upload.title.ChannelRemote')"
          icon="bi:upload"
          @click="destinationSelected({ type: 'remoteChannel' })"
        >
          <template #image>
            <div class="color-blue-solid" :class="$style.imageBackground">
              <Icon icon="bi:box-arrow-in-down-left" :class="$style.icon" />
            </div>
          </template>
          {{ t('modals.upload.ChannelRemote') }}
        </Card>
      </Layout>

      <!-- Page 2 -->

      <Layout
        v-else-if="state.page === 'uploadFiles'"
        stack
      >
        <ChannelUpload
          v-if="state.uploadDestination?.type === 'channel'"
          ref="channelUpload"
          :filter="state.uploadDestination.filter"
          :channel="state.uploadDestination?.channel || null"
        />

        <LibraryUpload
          v-if="state.uploadDestination?.type === 'library'"
          v-model="privacyLevel"
        />

        <RemoteSearchForm
          v-if="state.uploadDestination?.type === 'remoteChannel'"
          initial-type="both"
          :show-submit="false"
          :standalone="false"
          redirect
          @subscribed="isOpen = false"
        />
        {{ state.files }}
      </Layout>
    </Transition>

    <template #actions>
      <Spacer grow />
      <Button
        v-if="state.page === 'uploadFiles' && !channelUpload && state.uploadDestination?.type !== 'remoteChannel'"
        class="color-default-solid"
        @click="() => { isOpen = false }"
      >
        {{ t('components.channels.UploadModal.button.finishLater') }}
      </Button>
      <Button
        v-if="channelUpload"
        class="color-primary-solid"
        @click="() => { channelUpload.publish(); isOpen = false }"
      >
        {{ t('components.channels.UploadModal.button.publish') }}
      </Button>
      <Button
        v-if="state.uploadDestination?.type === 'remoteChannel'"
        form="remote-search"
        type="submit"
        class="color-primary-solid"
      >
        <Icon icon="bi:bookmark-check-fill" />
        {{ t('components.channels.UploadModal.button.subscribe') }}
      </Button>
    </template>
  </Modal>
</template>

<style module>
.imageBackground {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon {
  font-size: 6em;
}
</style>

<style>
.v-enter-active,
.v-leave-active {
  transition: all 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;

  &.forward {
    transform: translateX(100%);
  }

  &.backward {
    transform: translateX(-100%);
  }
}

.v-leave-to {
  &.forward {
    transform: translateX(100%);
  }

  &.backward {
    transform: translateX(-100%);
  }
}
</style>
